import React, { useState } from 'react';
import './ChartOfAccountsCardView.css';

const highlight = (text, term) => {
    if (!term || !text || term.trim() === '') return text;
    try {
        const re = new RegExp(`(${term.trim()})`, 'gi');
        const parts = String(text).split(re);
        return parts.map((p, i) => re.test(p) ? <mark key={i} className="search-highlight">{p}</mark> : p);
    } catch {
        return text;
    }
};

// ── Inline SVG icons (keep MUI dependency-free for this file) ──
const ExpandMoreIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    </svg>
);
const ExpandLessIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
    </svg>
);
const EditIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
);
const DeleteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
);

const CATEGORY_COLORS = [
    '#e3f2fd', '#e8f5e9', '#fff8e1',
    '#f3e5f5', '#ffebee', '#e0f7fa',
];

const getCategoryColor = category => {
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
        hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length];
};

/**
 * Card view for Chart of Accounts.
 * Accepts accounts in the new API shape: { id, code, name, arabicName, type, currency, ... }
 */
const ChartOfAccountsCardView = ({ accounts = [], onEdit, onDelete }) => {
    const [expandedCategories, setExpandedCategories] = useState({});
    const [visibleCounts, setVisibleCounts] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const toggleCategory = cat =>
        setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

    const showMore = (cat, e) => {
        e.stopPropagation();
        setVisibleCounts(prev => ({ ...prev, [cat]: (prev[cat] || 5) + 10 }));
    };

    const filtered = accounts.filter(a => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            a.code?.toLowerCase().includes(q) ||
            a.name?.toLowerCase().includes(q) ||
            a.arabicName?.toLowerCase().includes(q) ||
            a.type?.toLowerCase().includes(q)
        );
    });

    // Group by type: HEADER / POSTING
    const grouped = filtered.reduce((acc, a) => {
        const cat = a.type === 'HEADER' ? 'Header Accounts' : 'Posting Accounts';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(a);
        return acc;
    }, {});

    const renderItem = (account, index) => (
        <div key={account.id ?? index} className="account-item" onClick={() => onEdit && onEdit(account)}>
            <div className="account-code" title={account.code}>
                {highlight(account.code ?? 'N/A', searchTerm)}
            </div>
            <div className="account-name" title={account.arabicName || account.name}>
                {highlight(account.arabicName || account.name || 'Unnamed', searchTerm)}
            </div>
            <div className="account-balance">
                {account.currency?.code ?? '—'}
            </div>
            <div className="account-actions">
                {onEdit && (
                    <button className="btn-icon" onClick={e => { e.stopPropagation(); onEdit(account); }} title="Edit">
                        <EditIcon />
                    </button>
                )}
                {onDelete && (
                    <button className="btn-icon danger" onClick={e => { e.stopPropagation(); onDelete(account.id); }} title="Deactivate">
                        <DeleteIcon />
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="card-view-container">
            <div className="card-actions">
                <div className="search-container">
                    <i className="fas fa-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                    <input
                        type="text"
                        placeholder="Search accounts…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                        style={{ flex: 1, padding: '8px 12px 8px 35px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                </div>
            </div>

            {Object.keys(grouped).length === 0 ? (
                <div className="no-results">
                    <p>No accounts found{searchTerm ? ' — try a different search term' : ''}.</p>
                </div>
            ) : (
                <div className="card-view-grid">
                    {Object.entries(grouped).map(([category, cats]) => {
                        const isExpanded = expandedCategories[category];
                        const visible = visibleCounts[category] || 5;
                        const display = isExpanded ? cats.length : Math.min(visible, cats.length);
                        const hasMore = display < cats.length;

                        return (
                            <div
                                key={category}
                                className="category-card"
                                style={{ backgroundColor: getCategoryColor(category) }}
                            >
                                <div className="category-header" onClick={() => toggleCategory(category)} style={{ cursor: 'pointer' }}>
                                    <div>
                                        <h3>{category}</h3>
                                        <span className="account-count">{cats.length} accounts</span>
                                    </div>
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </div>

                                <div className="account-list">
                                    {cats.slice(0, display).map((a, i) => renderItem(a, i))}
                                    {hasMore && !isExpanded && (
                                        <div className="view-more" onClick={e => showMore(category, e)}>
                                            + {cats.length - display} more accounts
                                        </div>
                                    )}
                                </div>

                                <div className="category-total">
                                    <span>{cats.length} accounts</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ChartOfAccountsCardView;
